import { StandardEvents, CartErrorEvent } from '@shopify/events';

/**
 * CHAMFER — cart drawer behaviour that sits alongside Horizon's own:
 * accessible announcements, drawer and checkout analytics, a duplicate-submit
 * guard, and the visual hoist of the line the customer just added.
 *
 * Checkout navigation is entirely native. The checkout control is a plain
 * submit button inside <form id="cart-form" action="/cart" method="post">, so
 * the browser performs the POST. Nothing here calls preventDefault on a first
 * valid submission, and no analytics call is awaited before navigation.
 */

const LIVE_REGION_ID = 'cf-cart-live-region';
const SUBMIT_RELEASE_MS = 8000;

/**
 * Pushes an analytics event onto the store's existing dataLayer, when there is
 * one, and mirrors it as a DOM event so other tags can subscribe.
 * Synchronous and best-effort: never a purchase or conversion event, and never
 * something the checkout handoff waits on.
 * @param {string} name
 * @param {Record<string, unknown>} payload
 */
function cfTrack(name, payload = {}) {
  try {
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: name, ...payload });
    }
    document.dispatchEvent(new CustomEvent(`cf:${name}`, { detail: payload, bubbles: true }));
  } catch (error) {
    console.warn('[cf-cart] analytics push failed', error);
  }
}

/** @param {string} message */
function announce(message) {
  const region = document.getElementById(LIVE_REGION_ID);
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 60);
}

/** @param {string} message */
function showCheckoutError(message) {
  for (const node of document.querySelectorAll('[data-cf-cart-error]')) {
    if (!(node instanceof HTMLElement)) continue;
    node.textContent = message;
    node.hidden = false;
  }
  announce(message);
}

function clearCheckoutError() {
  for (const node of document.querySelectorAll('[data-cf-cart-error]')) {
    if (!(node instanceof HTMLElement)) continue;
    node.textContent = '';
    node.hidden = true;
  }
}

/** @returns {any} */
function readCartSnapshot() {
  const node = document.querySelector('[data-cf-cart-snapshot]');
  if (!(node instanceof HTMLElement) || !node.textContent) return null;
  try {
    return JSON.parse(node.textContent);
  } catch (error) {
    return null;
  }
}

// -- Drawer open -----------------------------------------------------------
document.addEventListener('theme-drawer:open', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement) || target.id !== 'cart-drawer') return;

  const snapshot = readCartSnapshot();
  cfTrack('cart_drawer_open', {
    currency: snapshot?.currency,
    value: snapshot?.value,
    items: snapshot?.items ?? [],
  });
});

// -- Cart update announcements --------------------------------------------
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const cartEvent = /** @type {any} */ (event);
  clearCheckoutError();
  releaseSubmitGuards();

  cartEvent.promise
    ?.then(({ detail }) => {
      if (detail?.didError) return;
      if (detail?.source === 'cf-cart-upsell') return; // already announced with product names

      if (cartEvent.action === 'add') {
        announce('Item added to your order.');
      } else if (cartEvent.action === 'remove') {
        announce('Item removed from your order.');
        const snapshot = readCartSnapshot();
        cfTrack('remove_from_cart', { currency: snapshot?.currency });
      }
    })
    .catch(() => {
      /* handled by the dispatching component */
    });
});

// -- Cart errors -----------------------------------------------------------
document.addEventListener(CartErrorEvent.eventName, (event) => {
  const message = /** @type {any} */ (event)?.detail?.error || 'We could not update your order.';
  showCheckoutError(message);
  cfTrack('cart_error', { message });
});

// -- Checkout handoff ------------------------------------------------------
// Observes the native submit; it never cancels or delays it. The first valid
// submission always proceeds. Only a second submission of a form already in
// flight is cancelled.
function releaseSubmitGuards() {
  for (const form of document.querySelectorAll('form[data-cf-submitting]')) {
    if (form instanceof HTMLElement) form.removeAttribute('data-cf-submitting');
  }
  for (const button of document.querySelectorAll('[data-cf-checkout][aria-busy], .cart__checkout-button[aria-busy]')) {
    if (button instanceof HTMLElement) button.removeAttribute('aria-busy');
  }
}

/**
 * @param {SubmitEvent} event
 * @returns {HTMLElement | null} The checkout submitter, when this is a checkout submission.
 */
function checkoutSubmitter(event) {
  const submitter =
    /** @type {HTMLElement | null} */ (event.submitter) ??
    (document.activeElement instanceof HTMLElement ? document.activeElement : null);

  if (submitter?.getAttribute?.('name') === 'checkout') return submitter;
  return null;
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement)) return;
  if (form.id !== 'cart-form') return;

  const submitter = checkoutSubmitter(/** @type {SubmitEvent} */ (event));
  if (!submitter) return; // an "update cart" submit, not a checkout handoff

  // Duplicate submission of a handoff already in flight — and only that.
  if (form.dataset.cfSubmitting === 'true') {
    event.preventDefault();
    return;
  }

  form.dataset.cfSubmitting = 'true';
  submitter.setAttribute('aria-busy', 'true');

  const snapshot = readCartSnapshot();
  cfTrack('begin_checkout', {
    currency: snapshot?.currency,
    value: snapshot?.value,
    items: snapshot?.items ?? [],
  });
  announce('Taking you to secure checkout.');

  // If navigation never happens (offline, blocked), hand the control back.
  window.setTimeout(releaseSubmitGuards, SUBMIT_RELEASE_MS);
});

window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  releaseSubmitGuards();
});

// The upsell's checkbox and select controls live inside the cart form. Enter on
// one of them would otherwise trigger implicit submission and send the customer
// to checkout by accident.
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter') return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  if (!target.closest('cf-cart-upsell')) return;
  if (!target.matches('select, input[type="checkbox"]')) return;
  event.preventDefault();
});

// -- "Newly added first" ---------------------------------------------------
// Shopify decides where a new line lands in cart.items. Rather than reorder
// the DOM (which would desynchronise the line indexes every cart component
// relies on), the just-added line is hoisted visually with CSS order and
// marked, so it reads as the item the customer just chose.
const FOCUS_KEY_STORE = 'cf-focus-line-keys';

/** @returns {string[]} */
function readFocusKeys() {
  try {
    const raw = sessionStorage.getItem(FOCUS_KEY_STORE);
    return raw ? JSON.parse(raw) : [];
  } catch (error) {
    return [];
  }
}

/** @param {string[]} keys */
function writeFocusKeys(keys) {
  try {
    sessionStorage.setItem(FOCUS_KEY_STORE, JSON.stringify(keys));
  } catch (error) {
    /* private mode - the hoist is a nicety, not a requirement */
  }
}

function applyFocusOrder() {
  const keys = readFocusKeys();
  for (const list of document.querySelectorAll('[data-cf-lines]')) {
    for (const row of list.querySelectorAll('[data-key]')) {
      if (!(row instanceof HTMLElement)) continue;
      const isFocus = keys.includes(row.dataset.key ?? '');
      row.classList.toggle('cf-line--focus', isFocus);
      row.style.order = isFocus ? '-1' : '';
    }
  }
}

/** @returns {string[]} */
function currentLineKeys() {
  return Array.from(document.querySelectorAll('[data-cf-lines] [data-key]'))
    .map((row) => (row instanceof HTMLElement ? row.dataset.key ?? '' : ''))
    .filter(Boolean);
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const cartEvent = /** @type {any} */ (event);
  if (cartEvent.action !== 'add') return;

  const before = new Set(currentLineKeys());

  cartEvent.promise
    ?.then(() => {
      // Let the section morph settle before reading the new rows.
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          const added = currentLineKeys().filter((key) => !before.has(key));
          if (added.length) writeFocusKeys(added);
          applyFocusOrder();
        });
      });
    })
    .catch(() => {
      /* handled elsewhere */
    });
});

document.addEventListener('theme-drawer:open', () => {
  window.requestAnimationFrame(applyFocusOrder);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', applyFocusOrder, { once: true });
} else {
  applyFocusOrder();
}